import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith, throttleTime } from 'rxjs/operators';
import { filterNulls } from '../utils/filter-nulls';

type ObserveResizeKeys =
  | 'offsetLeft'
  | 'offsetTop'
  | 'offsetWidth'
  | 'offsetHeight'
  | 'clientLeft'
  | 'clientTop'
  | 'clientWidth'
  | 'clientHeight';

export type ObserveResizeEvent<TKeys extends ObserveResizeKeys> = Record<TKeys, number>;

@Directive({
  selector: '[observeResize]',
  standalone: false,
})
export class ObserveResizeDirective<TKeys extends string = string>
  implements AfterViewInit, OnDestroy
{
  @Input({ required: true }) observe: ReadonlyArray<TKeys & ObserveResizeKeys>;

  @Output() observeResize = new EventEmitter<ObserveResizeEvent<TKeys & ObserveResizeKeys>>();

  private resizeSubscription?: Subscription;

  constructor(@Host() private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    const resize$ = new Observable<void>(subscriber => {
      const resizeObserver = new ResizeObserver(() => subscriber.next());

      resizeObserver.observe(this.el.nativeElement);

      return () => resizeObserver.disconnect();
    });

    this.resizeSubscription = resize$
      .pipe(
        startWith(),
        throttleTime(600, undefined, { leading: true, trailing: true }),
        map(_ => this.el.nativeElement),
        filterNulls(),
        map(
          el =>
            Object.fromEntries(this.observe.map(k => [k, el[k]] as const)) as ObserveResizeEvent<
              TKeys & ObserveResizeKeys
            >,
        ),
        distinctUntilChanged((a, b) => this.observe.every(k => a[k] === b[k])),
      )
      .subscribe(ev => this.observeResize.emit(ev));
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
  }
}
